<template>
  <div class="flex items-center">
    <Badge v-if="creationAuditLog" variant="neutral" size="md">
      {{
        t('created', {
          createdBy: `${creationAuditLog.user?.firstName} ${creationAuditLog.user?.lastName}`,
          createdAt: formatCreatedAt(creationAuditLog.createdAt),
        })
      }}
    </Badge>
    <div v-if="status === 'pending'" class="flex justify-center py-28">
      <Spinner />
    </div>
    <div v-if="status === 'error'" class="flex justify-center py-28">
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import Badge from '@/components/Badge.vue';
import { useDateFormat } from '@/hooks/use-date-format';
import { useAuditLogByEntity } from '@/queries/use-audit-log';
import { AuditLogOperation, type AuditLogEntityType } from '@carvia/ros-client-types';
import { DateTime } from 'luxon';
import { computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  entityType: AuditLogEntityType;
  entityId: string;
}>();

const { t } = useI18n();

const dateFormat = useDateFormat();

const formatCreatedAt = (createdAt: Date | string) => {
  const format = unref(dateFormat);
  return createdAt instanceof Date
    ? DateTime.fromJSDate(createdAt).toFormat(format)
    : DateTime.fromISO(createdAt).toFormat(format);
};

const { data, status, error } = useAuditLogByEntity({
  entityType: props.entityType,
  entityId: props.entityId,
  operation: AuditLogOperation.CREATE,
});

const creationAuditLog = computed(() => {
  return data.value?.pages[0]?.items.find(({ operation }) => operation === 'create');
});
</script>

<i18n lang="json">
{
  "en": {
    "created": "Created by {createdBy} on {createdAt}"
  },
  "de": {
    "created": "Erstellt von {createdBy} am {createdAt}"
  },
  "es": {
    "created": "Creado por {createdBy} el {createdAt}"
  }
}
</i18n>
